import React, { useMemo } from 'react';
import '../styles/Pagination.css';
import { colour } from '../styles/GlobalStyles';
import { Chevron } from '../assets/icons';

const DOTS = 'DOTS';

const range = (start, end) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

/**
 * Pagination component
 *
 * Props:
 *  - total (number)        : total number of data items
 *  - limit (number)        : items per page
 *  - currentPage (number)  : currently selected page (1‑based)
 *  - onPageChange (func)   : handler to call with the new page number
 *  - siblingCount (number) : how many pages to show on either side of current (default = 1)
 */
function Pagination({
  total,
  limit,
  currentPage,
  onPageChange,
  siblingCount = 1,
}) {
    const totalPages = Math.ceil(total / limit);

    const paginationRange = useMemo(() => {
        // total numbers to show = first + last + current + 2*siblings + 2 dots
        const totalPageNumbers = siblingCount * 2 + 5;

        // if everything fits, show all pages
        if (totalPageNumbers >= totalPages) {
            return range(1, totalPages);
        }

        const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
        const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

        const showLeftDots = leftSiblingIndex > 2;
        const showRightDots = rightSiblingIndex < totalPages - 1;

        const firstPageIndex = 1;
        const lastPageIndex = totalPages;

        // no left dots, but right dots
        if (!showLeftDots && showRightDots) {
            const leftItemCount = 3 + 2 * siblingCount;
            const leftRange = range(1, leftItemCount);

            return [...leftRange, DOTS, lastPageIndex];
        }

        // left dots, but no right dots
        if (showLeftDots && !showRightDots) {
            const rightItemCount = 3 + 2 * siblingCount;
            const rightRange = range(totalPages - rightItemCount + 1, totalPages);

            return [firstPageIndex, DOTS, ...rightRange];
        }

        // left dots AND right dots
        if (showLeftDots && showRightDots) {
            const middleRange = range(leftSiblingIndex, rightSiblingIndex);
            return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
        }
    }, [totalPages, siblingCount, currentPage]);

    if (totalPages === 0) {
        return null;
    }

  return (
    <div className="pagination-outer-container">
        <div className="pagination-inner-container">
            {
                currentPage > 1 &&
                <div 
                    className="pagination-chevron-container cursor-pointer"
                    onClick={() => onPageChange(currentPage - 1)}
                >
                    <Chevron
                        fill={colour.gray06}
                        className="pagination-chevron reverse"
                    />
                </div>
            }
            {
                paginationRange.map((pageNumber, idx) =>
                    pageNumber === DOTS ?
                    <span key={idx} className="pagination-dots">
                        &hellip;
                    </span>
                    :
                    <div
                        className={`pagination-item ${pageNumber === currentPage ? "pagination-item-active bg-colour-dark" : "cursor-pointer"}`}
                        key={idx}
                        onClick={() => onPageChange(pageNumber)}
                    >
                        <span className={` ${pageNumber === currentPage ? "body-semibold colour-white" : "body-regular colour-primary"}`}>
                            {pageNumber}
                        </span>
                    </div>
                )
            }
            {
                currentPage < totalPages &&
                <div 
                    className="pagination-chevron-container cursor-pointer"
                    onClick={() => onPageChange(currentPage + 1)}
                >
                    <Chevron
                        fill={colour.gray06}
                        className="pagination-chevron"
                    />
                </div>
            }
        </div>
    </div>
  );
}

export default Pagination;